import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

/** Shared interaction for annotations; content and business actions stay with the caller. */
export default function useActionableAnnotation<T extends HTMLElement = HTMLElement>({ enabled, openOnMount = false, contentKey, triggerRef, onDismiss }: {
  enabled: boolean;
  openOnMount?: boolean;
  contentKey?: unknown;
  triggerRef?: RefObject<HTMLElement>;
  onDismiss?: (event?: Event | React.SyntheticEvent) => void;
}) {
  const [preview, setPreview] = useState(false);
  const [pinned, setPinned] = useState(false);
  const anchor = useRef<T>(null);
  const content = useRef<HTMLDivElement>(null);
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  const close = useCallback((event?: Event | React.SyntheticEvent) => {
    setPinned(false); setPreview(false); dismiss.current?.(event);
  }, []);

  useEffect(() => {
    if (!enabled) setPinned(false);
    else if (openOnMount && contentKey) setPinned(true);
  }, [enabled, openOnMount, contentKey]);
  useEffect(() => {
    if (!enabled) return;
    const trigger = triggerRef?.current;
    const pin = (event: MouseEvent) => {
      event.preventDefault(); event.stopPropagation(); setPinned(true);
    };
    trigger?.addEventListener('click', pin, true);
    return () => trigger?.removeEventListener('click', pin, true);
  }, [enabled, triggerRef]);
  useEffect(() => {
    if (!pinned) return;
    const clickAway = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!anchor.current?.contains(target) && !content.current?.contains(target) && !triggerRef?.current?.contains(target)) close(event);
    };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') close(event); };
    document.addEventListener('click', clickAway, true);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('click', clickAway, true); document.removeEventListener('keydown', escape); };
  }, [pinned, triggerRef, close]);

  return { anchor, content, pinned, open: preview || pinned, close,
    pin: () => setPinned(true), preview: () => setPreview(true), hidePreview: () => setPreview(false) };
}
